import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Box,
  Typography,
  Alert,
  useTheme,
  useMediaQuery,
  Tooltip
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon
} from "@mui/icons-material";
import { useState } from "react";

// Helper function to get CSRF token from cookie
const getCsrfToken = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      return value;
    }
  }
  return null;
};

// Save a single option selection
const saveOptionSelection = async ({ quoteId, eventId, optionId }) => {
  const response = await fetch(
    process.env.REACT_APP_BACKEND_API_URL,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": getCsrfToken() || "",
      },
      credentials: "include",
      body: JSON.stringify({
        query: `
          mutation SelectEventOption($quoteId: UUID!, $eventId: Int!, $optionId: Int!) {
            selectEventOption(quoteId: $quoteId, eventId: $eventId, optionId: $optionId) {
              responseCode
              message
            }
          }
        `,
        variables: {
          quoteId,
          eventId: parseInt(eventId, 10),
          optionId: parseInt(optionId, 10)
        },
      }),
    }
  );

  const json = await response.json();
  if (!response.ok || json.errors) {
    throw new Error(json.errors?.[0]?.message || "Failed to save option selection");
  }

  return json.data.selectEventOption;
};

// Define the mutation function
const updateQuoteStatus = async ({ quoteId, booleanResponse }) => {
  const response = await fetch(
    process.env.REACT_APP_BACKEND_API_URL,
    {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken() || "",
    },
    credentials: "include",
    body: JSON.stringify({
      query: `
        mutation UpdateQuote($quoteId: UUID!, $booleanResponse: Boolean!, $reason: String) {
          quoteResponse(quoteId: $quoteId, booleanResponse: $booleanResponse, reason: $reason) {
            responseCode
            message
          }
        }
      `,
      variables: {
        quoteId,
        booleanResponse,
        reason: booleanResponse
          ? "User accepted the quote"
          : "User rejected the quote",
      },
    }),
  });

  const json = await response.json();
  if (!response.ok || json.errors) {
    throw new Error(json.errors?.[0]?.message || "Something went wrong");
  }

  return json.data.quoteResponse;
};

// Reassurance shown next to the accept button so clients know accepting
// isn't a payment or a final commitment.
const NoPaymentNote = ({ sx = {} }) => (
  <Typography
    variant="body2"
    sx={{ color: '#666', textAlign: 'center', ...sx }}
  >
    No payment required yet - you'll confirm details and deposit next.
  </Typography>
);

const AcceptRejectButton = ({ quoteId, isAccepted, allOptionsSelected = true, stickyMode = false, localSelections = {}, eventsWithOptions = [] }) => {
  const [message, setMessage] = useState("");
  const [hideButtons] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Check if accept button should be disabled
  const isAcceptDisabled = !allOptionsSelected;

  // Use React Query's mutation
  const mutation = useMutation({
    mutationFn: updateQuoteStatus,
    onSuccess: (data) => {
      // Redirect immediately to confirm stage on success
      if (data.responseCode === 200) {
        window.location.href = `/quote/${quoteId}/confirm`;
      } else {
        setMessage(data.message);
        setIsSaving(false);
      }
    },
    onError: (error) => {
      setMessage(error.message);
      setIsSaving(false);
    },
  });

  // Handle accept - save all selections first, then accept the quote
  const handleAccept = async () => {
    setIsSaving(true);
    setMessage("");

    try {
      // Save all local selections to the server
      for (const event of eventsWithOptions) {
        const optionId = localSelections[event.id];
        if (optionId) {
          await saveOptionSelection({ quoteId, eventId: event.id, optionId });
        }
      }

      // Then accept the quote
      mutation.mutate({ quoteId, booleanResponse: true });
    } catch (error) {
      setMessage(error.message || "Failed to save selections");
      setIsSaving(false);
    }
  };

  const isLoading = isSaving || mutation.isLoading;

  // Sticky mode - just show the button
  if (stickyMode) {
    const buttonContent = (
      <Button
        variant="contained"
        size="large"
        startIcon={isAcceptDisabled ? <WarningIcon /> : <CheckIcon />}
        onClick={handleAccept}
        disabled={isLoading || isAcceptDisabled}
        sx={{
          py: { xs: 1.5, sm: 2 },
          px: { xs: 3, sm: 5 },
          fontSize: { xs: '1rem', sm: '1.1rem' },
          fontWeight: 'bold',
          backgroundColor: isAcceptDisabled ? '#9e9e9e' : '#380045',
          minWidth: { xs: '100%', sm: '280px' },
          boxShadow: isAcceptDisabled
            ? '0 2px 8px rgba(0, 0, 0, 0.15)'
            : '0 4px 12px rgba(56, 0, 69, 0.3)',
          '&:hover': {
            backgroundColor: isAcceptDisabled ? '#9e9e9e' : '#2d0036',
            boxShadow: isAcceptDisabled
              ? '0 2px 8px rgba(0, 0, 0, 0.15)'
              : '0 6px 16px rgba(56, 0, 69, 0.4)',
          },
          '&.Mui-disabled': {
            backgroundColor: '#9e9e9e',
            color: 'white'
          },
          transition: 'all 0.3s ease'
        }}
      >
        {isLoading ? 'Processing...' : isAcceptDisabled ? 'Select Options First' : 'Accept Quote'}
      </Button>
    );

    if (isAcceptDisabled) {
      return (
        <Tooltip
          title="Please select an option for each event before accepting the quote"
          arrow
          placement="top"
        >
          <span>{buttonContent}</span>
        </Tooltip>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: { xs: '100%', sm: 'auto' }
        }}
      >
        {buttonContent}
        <NoPaymentNote sx={{ mt: 1 }} />
      </Box>
    );
  }

  return (
    !isAccepted ? (
      <Box sx={{ width: "100%" }}>
        {!hideButtons && (
          <Box>
            {/* Main Call to Action */}
            <Typography
              variant={isMobile ? "h5" : "h4"}
              gutterBottom
              sx={{
                textAlign: 'center',
                color: '#380045',
                fontWeight: 'bold',
                mb: 2
              }}
            >
              Ready to Book Your Event?
            </Typography>

            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                color: '#666',
                mb: 4,
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Accepting simply moves you to the next step, where you'll confirm your
              details and pay a deposit to secure your booking. Nothing is charged now.
            </Typography>

            {/* Options Warning */}
            {isAcceptDisabled && (
              <Alert
                severity="warning"
                icon={<WarningIcon />}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: '#fff3e0',
                  '& .MuiAlert-message': { width: '100%' }
                }}
              >
                <Typography variant="body1" fontWeight="500">
                  Please select an option for each event before accepting the quote.
                </Typography>
              </Alert>
            )}

            {/* Accept Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Tooltip
                title={isAcceptDisabled ? "Select all options first" : ""}
                arrow
                placement="top"
              >
                <span>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={isAcceptDisabled ? <WarningIcon /> : <CheckIcon />}
                    onClick={handleAccept}
                    disabled={isLoading || isAcceptDisabled}
                    sx={{
                      py: { xs: 2, sm: 2.5 },
                      px: { xs: 4, sm: 6 },
                      fontSize: { xs: '1.1rem', sm: '1.2rem' },
                      fontWeight: 'bold',
                      backgroundColor: isAcceptDisabled ? '#9e9e9e' : '#380045',
                      minWidth: { xs: '100%', sm: '300px' },
                      boxShadow: isAcceptDisabled
                        ? '0 2px 8px rgba(0, 0, 0, 0.15)'
                        : '0 4px 12px rgba(56, 0, 69, 0.3)',
                      '&:hover': {
                        backgroundColor: isAcceptDisabled ? '#9e9e9e' : '#2d0036',
                        boxShadow: isAcceptDisabled
                          ? '0 2px 8px rgba(0, 0, 0, 0.15)'
                          : '0 6px 16px rgba(56, 0, 69, 0.4)',
                        transform: isAcceptDisabled ? 'none' : 'translateY(-2px)',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#9e9e9e',
                        color: 'white'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {isLoading ? 'Processing...' : isAcceptDisabled ? 'Select Options First' : 'Accept Quote'}
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {/* Reassurance */}
            {!isAcceptDisabled && <NoPaymentNote sx={{ mt: -2, mb: 4 }} />}

            {/* Contact Option */}
            <Box
              sx={{
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                p: 3,
                textAlign: 'center'
              }}
            >
              <Typography variant="body1" sx={{ mb: 1.5, fontWeight: '500' }}>
                Have questions or want to discuss the quote?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We're here to help! Get in touch with us and we'll be happy to adjust anything.
              </Typography>
              <Button
                variant="outlined"
                href="mailto:info@imperialdrummers.com"
                sx={{
                  borderColor: '#380045',
                  color: '#380045',
                  fontWeight: 'bold',
                  '&:hover': {
                    borderColor: '#2d0036',
                    backgroundColor: 'rgba(56, 0, 69, 0.05)'
                  }
                }}
              >
                Email Us
              </Button>
            </Box>
          </Box>
        )}

        {message && (
          <Alert
            severity="success"
            sx={{
              mt: 3,
              textAlign: 'center',
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="body1" fontWeight="bold">
              {message}
            </Typography>
          </Alert>
        )}

        {mutation.isError && (
          <Alert
            severity="error"
            sx={{
              mt: 3,
              textAlign: 'center',
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="body1">
              Error: {mutation.error.message}
            </Typography>
          </Alert>
        )}
      </Box>
    ) : (
      <Alert
        severity="info"
        sx={{
          textAlign: 'center',
          backgroundColor: '#e3f2fd',
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
      >
        <Typography variant="body1" fontWeight="bold">
          This quote has already been accepted.
        </Typography>
      </Alert>
    )
  );
};

export default AcceptRejectButton;